import { useRef, useState } from 'react';
import {
  addToast,
  Button,
  OutlinedSelect,
  TextOutlinedInput,
} from '@octano/global-ui';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import ClassroomFormModal, {
  ClassroomFormModalMethods,
} from './modal/ClassroomFormModal';
import {
  ITEMS_PER_PAGE_DEFAULT,
  PAGE_DEFAULT,
  useClassrooms,
} from './Loaders/ClassroomsLoader';
import { useFormData } from './SearchControlsLoader';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { SearchControlsForm } from '../types';
import { downloadClassrooms } from '../api';
import { downloadFromBlob } from '../../../../utils/blob';

const DEFAULT_VALUES = {
  name: null,
  code: null,
  type: null,
  campus: null,
  state: null,
};

export default function SearchControls() {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_CLASSROOMS);
  const classroomModalRef = useRef<ClassroomFormModalMethods>(null);
  const { refresh } = useClassrooms();
  const { data, refresh: refreshFilterOptions } = useFormData();

  /**
   * Al guardar una sala hay que refrescar DOS cosas: el listado y las opciones
   * de los filtros. Las opciones salen de `filters-data`, que se pide una sola
   * vez al montar la vista: sin este segundo refresh, el código de la sala
   * recién creada no aparece en el desplegable, y el de una sala editada sigue
   * ofreciendo el valor viejo (que ya no devuelve ninguna fila).
   */
  const reloadClassroomList = () => {
    refresh();
    refreshFilterOptions();
  };

  const handleAddClassroom = () => {
    classroomModalRef.current?.open();
  };
  const { updateQuery, loading, query } = useClassrooms();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { control, handleSubmit, reset } = useForm<SearchControlsForm>({
    defaultValues: DEFAULT_VALUES,
  });

  const codeOptions = useMemo(() => {
    return data?.classroomCodes.map((code) => ({
      value: code.code,
      label: code.code,
    }));
  }, [data]);

  const typeOptions = useMemo(() => {
    return data?.classroomTypes.map((type) => ({
      value: type.id,
      label: type.name,
    }));
  }, [data]);

  const campusOptions = useMemo(() => {
    return data?.campuses.map((campus) => ({
      value: campus.id,
      label: campus.name,
    }));
  }, [data]);

  const onSubmit = useCallback(
    (data: SearchControlsForm) => {
      updateQuery({
        items: ITEMS_PER_PAGE_DEFAULT,
        page: PAGE_DEFAULT,
        name: data.name?.trim() as string,
        code: data.code?.value as string,
        type: data.type?.value as number,
        campus: data.campus?.value as number,
        state: data.state ? data.state.value.toString() : undefined,
      });
    },
    [updateQuery],
  );

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    // Se manda la query VIGENTE del listado, no lo que hay tipeado en el
    // formulario: el archivo espeja lo que el usuario está viendo.
    const result = await downloadClassrooms(query);
    if (result.status === 204) {
      addToast({
        color: 'warning',
        icon: 'warning',
        text: t('download.empty'),
      });
    } else if (result.error || !result.data) {
      addToast({
        color: 'danger',
        icon: 'error',
        text: t('download.error'),
      });
    } else {
      downloadFromBlob(result.data, `${t('download.fileName')}.xlsx`);
      addToast({
        color: 'success',
        icon: 'success',
        text: t('download.success'),
      });
    }
    setIsDownloading(false);
  }, [query, t]);

  const handleClear = useCallback(() => {
    reset();
    updateQuery({
      items: ITEMS_PER_PAGE_DEFAULT,
      page: PAGE_DEFAULT,
    });
  }, [reset, updateQuery]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row xs={12}>
        <Col xs={12} lg={6} xl={3}>
          <TextOutlinedInput
            name="name"
            label={t('filters.name.label')}
            placeholder={t('filters.name.placeholder')}
            control={control}
            disabled={loading}
          />
        </Col>
        <Col xs={12} lg={6} xl={3}>
          <OutlinedSelect
            name="code"
            label={t('filters.code.label')}
            placeholder={t('filters.code.placeholder')}
            options={codeOptions || []}
            control={control}
            disabled={loading}
          />
        </Col>
        <Col xs={12} lg={6} xl={3}>
          <OutlinedSelect
            name="type"
            label={t('filters.type.label')}
            placeholder={t('filters.type.placeholder')}
            options={typeOptions || []}
            control={control}
            disabled={loading}
          />
        </Col>
        <Col xs={12} lg={6} xl={3}>
          <OutlinedSelect
            name="campus"
            label={t('filters.campus.label')}
            placeholder={t('filters.campus.placeholder')}
            options={campusOptions || []}
            control={control}
            disabled={loading}
          />
        </Col>
      </Row>
      <Row xs={12} className="d-flex justify-content-between">
        <Col xs={12} lg={6} xl={3}>
          <OutlinedSelect
            name="state"
            label={t('filters.state.label')}
            placeholder={t('filters.state.placeholder')}
            options={[
              { value: 'true', label: t('filters.states.enabled') },
              { value: 'false', label: t('filters.states.disabled') },
            ]}
            control={control}
            disabled={loading}
          />
        </Col>
        <Col xs={12} lg={6} xl={3}>
          <Row xs={12} className="d-flex align-items-center h-100 mt-1">
            <Col xs={6}>
              <Button
                text={t('filters.clean')}
                size="md"
                outlined
                fullwidth
                onClick={handleClear}
                disabled={loading}
              />
            </Col>
            <Col xs={6}>
              <Button
                text={t('filters.search')}
                type="submit"
                size="md"
                fullwidth
                disabled={loading}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row xs={12} className="d-flex justify-content-end mt-2 mb-5">
        <Col xs={12} lg={6} xl={3}>
          <Button
            type="button"
            text={t('download.button')}
            size="md"
            icon="download"
            outlined
            fullwidth
            loading={isDownloading}
            onClick={handleDownload}
          />
        </Col>
        <Col xs={12} lg={6} xl={3}>
          <Button
            text={t('filters.addClassroom')}
            size="md"
            fullwidth
            onClick={handleAddClassroom}
          />
        </Col>
      </Row>

      <ClassroomFormModal
        ref={classroomModalRef}
        onSave={reloadClassroomList}
      />
    </form>
  );
}
